import {
  GraphQLBoolean,
  GraphQLEnumType,
  GraphQLID,
  GraphQLInt,
  GraphQLList,
  GraphQLNonNull,
  GraphQLObjectType,
  GraphQLString
} from 'graphql'
import { dateTimeType } from './date-time-type'
import { userInput, UserInput } from './user-input'
import { AttackStatus, AttackStatusType } from './attack-status'
import { Application, ApplicationType } from './application'
import { Server, serverType } from './server'
import { SourceName, sourceNameType } from './source-names'

interface AttackEventDetails {
  findings?: string[]
  query?: string
  command?: string
  entryName?: string
  path?: string
  start?: number
  end?: number
  commandArgs?: string[]
  directory?: string
}

export enum AttackType {
  AUTOMATED = 'AUTOMATED',
  MANUAL = 'MANUAL'
}

export interface AttackEvent {
  id: string
  received: string
  sourceIP?: string
  sourceName?: SourceName | null
  result: AttackStatus
  displayName: string
  ruleUuid: string
  suppressed: boolean
  application: Partial<Application>
  server: Partial<Server>
  type: string
  userInput?: Partial<UserInput>
  details?: AttackEventDetails
  detectionType?: string
  url?: string
  attackType: AttackType
  attackers: string[]
  tags: string[]
  hasExclusion: boolean
}

export interface StackFrame {
  type: string
  description: string
  fileName: string
  lineNumber: number
  className: string
  methodName: string
  stackIndex: number
  shortSummary: string
}

const stackFrames = new GraphQLObjectType({
  name: 'StackFrames',
  fields: () => ({
    type: {
      description: 'The type of frame it is',
      type: GraphQLString
    },
    description: {
      description: 'The description of the frame',
      type: GraphQLString
    },
    fileName: {
      description: 'The file name associated with the frame',
      type: GraphQLString
    },
    lineNumber: {
      description: 'The line number associated with the frame',
      type: GraphQLInt
    },
    className: {
      description: 'The class name associated with the frame',
      type: GraphQLString
    },
    methodName: {
      description: 'The method name associated with the frame',
      type: GraphQLString
    },
    stackIndex: {
      description: 'The index of the stack',
      type: GraphQLInt
    },
    shortSummary: {
      description: 'A short summary of the frame',
      type: GraphQLString
    }
  })
})

export const eventDetailsType = new GraphQLObjectType({
  name: 'AttackEventDetails',
  fields: () => ({
    findings: {
      description:
        'The findings for attack events detected with symantic analysis',
      type: new GraphQLList(GraphQLString)
    },
    entryName: {
      description: 'The entry name for a zip-file-overwrite attack',
      type: GraphQLString
    },
    query: {
      description: 'A query string',
      type: GraphQLString
    },
    command: {
      description: 'A command string',
      type: GraphQLString
    },
    path: {
      description: 'A path string',
      type: GraphQLString
    },
    start: {
      description: 'The start index used to highlight the attack value',
      type: GraphQLInt
    },
    end: {
      description: 'The end index used to highlight the attack value',
      type: GraphQLInt
    },
    commandArgs: {
      description:
        'The command arguments for attack events detected with symantic analysis',
      type: new GraphQLList(GraphQLString)
    },
    directory: {
      description:
        'The directory name for attack events detected with symantic analysis',
      type: GraphQLString
    }
  })
})

const attackType = new GraphQLEnumType({
  name: 'AttackType',
  values: {
    AUTOMATED: {
      value: 'AUTOMATED'
    },
    MANUAL: {
      value: 'MANUAL'
    }
  }
})

const attackEventChapter = new GraphQLObjectType({
  name: 'AttackEventChapter',
  fields: () => ({
    type: {
      type: new GraphQLNonNull(GraphQLString)
    },
    introText: {
      type: GraphQLString
    },
    introTemplate: {
      type: GraphQLString
    },
    introVariables: {
      type: GraphQLString
    },
    body: {
      type: GraphQLString
    },
    bodyTemplate: {
      type: GraphQLString
    },
    bodyVariables: {
      type: GraphQLString
    }
  })
})

export const attackEventType = new GraphQLObjectType({
  name: 'AttackEvent',
  fields: () => ({
    id: {
      description: 'The unique ID for an attack event',
      type: new GraphQLNonNull(GraphQLID)
    },
    received: {
      description: 'Time the attack event was recieved',
      type: new GraphQLNonNull(dateTimeType)
    },
    sourceIP: {
      description: 'Source IP of the attack event',
      type: GraphQLString
    },
    result: {
      description: 'Status of the attack event',
      type: new GraphQLNonNull(AttackStatusType)
    },
    type: {
      description: 'Type of attack',
      type: new GraphQLNonNull(GraphQLString)
    },
    displayName: {
      description: 'Display string for the type of attack event',
      type: new GraphQLNonNull(GraphQLString)
    },
    ruleUuid: {
      description: 'Rule ID for the attack event',
      type: new GraphQLNonNull(GraphQLID)
    },
    suppressed: {
      description: 'Whether or not the attack is suppressed',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    application: {
      description: 'The application that was attacked',
      type: new GraphQLNonNull(ApplicationType)
    },
    server: {
      description: 'The server that was attacked',
      type: new GraphQLNonNull(serverType)
    },
    userInput: {
      description: 'The user input that generated the attack event',
      type: userInput
    },
    detectionType: {
      description:
        'The detection type for an attack event. Not available for events such as bot blockers.',
      type: GraphQLString
    },
    httpRequest: {
      description: 'The HTTP request for the attack event',
      type: httpRequest
    },
    callLocation: {
      description: 'The call location for the attack event',
      type: GraphQLString
    },
    file: {
      description: 'The file that contains the affected method',
      type: GraphQLString
    },
    method: {
      description: 'The method containing the affected code',
      type: GraphQLString
    },
    line: {
      description: 'The line number of the affected code',
      type: GraphQLInt
    },
    stackFrames: {
      description: 'The stack frames for the event',
      type: new GraphQLList(stackFrames)
    },
    lastCustomFrame: {
      description: 'The last frame of custom code',
      type: GraphQLInt
    },
    signature: {
      description: 'The attack event signature',
      type: GraphQLString
    },
    vector: {
      description: 'The attack vector',
      type: GraphQLString
    },
    details: {
      description:
        'The attack event details which are different for each type of attack',
      type: eventDetailsType
    },
    url: {
      description: 'The attack event URL',
      type: GraphQLString
    },
    attackType: {
      description: 'The attack type',
      type: new GraphQLNonNull(attackType)
    },
    attackers: {
      description: 'The list of attackers',
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    },
    sourceName: {
      description: 'Source name applied to this attack event',
      type: sourceNameType
    },
    tags: {
      description: 'Tags associated with this attack event',
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    },
    hasExclusion: {
      description: 'Indicates if a rule exclusion exists for this attack event',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    storyChapters: {
      type: new GraphQLNonNull(new GraphQLList(attackEventChapter))
    },
    requestChapters: {
      type: new GraphQLList(attackEventChapter)
    },
    hidden: {
      type: new GraphQLNonNull(GraphQLBoolean)
    }
  })
})

export interface HttpRequest {
  uri: string
  method: string
  queryString: string
  version: string
  body: string
  headers: string
  parameters: string
  valid: boolean
}
const httpRequest = new GraphQLObjectType({
  name: 'HttpRequest',
  fields: () => ({
    method: {
      description: 'The http request method',
      type: GraphQLString
    },
    queryString: {
      description: 'The http request querystring',
      type: GraphQLString
    },
    uri: {
      description: 'The http request uri',
      type: GraphQLString
    },
    version: {
      description: 'The http request version',
      type: GraphQLString
    },
    body: {
      description: 'The http request body',
      type: GraphQLString
    },
    headers: {
      description: 'The http request headers',
      type: GraphQLString
    },
    parameters: {
      description: 'The http request parameters',
      type: GraphQLString
    },
    valid: {
      description: 'The http request validity',
      type: GraphQLBoolean
    }
  })
})
